import React from 'react'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Flex } from '../../atoms/Flex'
import { Icon } from '../../atoms/Icon'
import { IconButton } from '../../atoms/IconButton'
import { Badge } from '../../atoms/Badge'
import { styled } from 'styled-components'
import type { Listing } from '@popety-io/types'

export interface SavedListingsGridTranslations {
  priceOnRequest?: string
  unknown?: string
  today?: string
  yesterday?: string
  daysAgo?: string
  weeksAgo?: string
  monthsAgo?: string
  yearsAgo?: string
  propertyImage?: string
  active?: string
  expired?: string
  removeFromSaved?: string
  perSqm?: string
  propertyListing?: string
  addressNotAvailable?: string
  rooms?: string
  sqm?: string
  built?: string
  listed?: string
}

const defaultTranslations: SavedListingsGridTranslations = {
  priceOnRequest: 'Price on request',
  unknown: 'Unknown',
  today: 'Today',
  yesterday: 'Yesterday',
  daysAgo: 'days ago',
  weeksAgo: 'weeks ago',
  monthsAgo: 'months ago',
  yearsAgo: 'years ago',
  propertyImage: 'Property image',
  active: 'Active',
  expired: 'Expired',
  removeFromSaved: 'Remove from saved',
  perSqm: '/m²',
  propertyListing: 'Property Listing',
  addressNotAvailable: 'Address not available',
  rooms: 'rooms',
  sqm: 'm²',
  built: 'built',
  listed: 'Listed',
}

export interface SavedListingsGridProps {
  listings: Listing[]
  onListingClick: (listing: Listing) => void
  onRemoveListing?: (listingId: number) => void
  translations?: SavedListingsGridTranslations
}

const GridContainer = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
  padding: 12px;
`

const ListingCard = styled(Box)`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  border: 1px solid #e5e7eb;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #d1d5db;
  }
`

const ImageContainer = styled(Box)`
  position: relative;
  width: 100%;
  height: 140px;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
`

const PropertyImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${ListingCard}:hover & {
    transform: scale(1.05);
  }
`

const ImagePlaceholder = styled(Flex)`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`

const StatusBadge = styled(Badge)<{ $isActive?: boolean }>`
  position: absolute;
  top: 8px;
  left: 8px;
  backdrop-filter: blur(8px);
  background: ${(props) =>
    props.$isActive ? 'rgba(34, 197, 94, 0.9)' : 'rgba(239, 68, 68, 0.9)'};
  color: white;
  border: none;
  font-weight: 600;
  padding: 2px 8px;
  font-size: 11px;
`

const RemoveButton = styled(IconButton)`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;

  ${ListingCard}:hover & {
    opacity: 1;
  }

  &:hover {
    background: white;
    transform: scale(1.1);
  }
`

const ContentSection = styled(Box)`
  padding: 10px;
`

const PriceRow = styled(Flex)`
  align-items: baseline;
  gap: 4px;
  margin-bottom: 4px;
`

const MainPrice = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.3px;
`

const PricePerM2 = styled(Text)`
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
`

const PropertyTitle = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  min-height: 16px;
`

const AddressRow = styled(Flex)`
  align-items: center;
  gap: 3px;
  margin-bottom: 8px;
`

const AddressText = styled(Text)`
  font-size: 11px;
  color: #6b7280;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FeatureGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
`

const FeatureItem = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`

const FeatureIcon = styled(Icon)`
  color: #9ca3af;
`

const FeatureValue = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  line-height: 1;
`

const FeatureLabel = styled(Text)`
  font-size: 10px;
  color: #9ca3af;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const DateRow = styled(Flex)`
  align-items: center;
  gap: 4px;
  padding-top: 8px;
  margin-top: 8px;
  border-top: 1px solid #f3f4f6;
`

const DateText = styled(Text)`
  font-size: 10px;
  color: #9ca3af;
`

export const SavedListingsGrid: React.FC<SavedListingsGridProps> = ({
  listings,
  onListingClick,
  onRemoveListing,
  translations: propTranslations,
}) => {
  const translations = { ...defaultTranslations, ...propTranslations }

  const formatPrice = (price?: number) => {
    if (!price || price <= 0) return translations.priceOnRequest
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price)
  }

  const formatPricePerM2 = (listing: Listing) => {
    const pricePerM2 =
      listing.price_per_square_meter ||
      (listing.price && listing.area && listing.price > 0 && listing.area > 0
        ? listing.price / listing.area
        : null)

    if (!pricePerM2 || pricePerM2 <= 0) return null

    return new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(pricePerM2)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return translations.unknown
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return translations.today
    if (diffDays === 1) return translations.yesterday
    if (diffDays < 7) return `${diffDays} ${translations.daysAgo}`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} ${translations.weeksAgo}`
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} ${translations.monthsAgo}`
    return `${Math.floor(diffDays / 365)} ${translations.yearsAgo}`
  }

  const handleRemoveClick = (e: React.MouseEvent, listingId: number) => {
    e.stopPropagation()
    onRemoveListing?.(listingId)
  }

  return (
    <GridContainer>
      {listings.map((listing) => (
        <ListingCard key={listing.id} onClick={() => onListingClick(listing)}>
          <ImageContainer>
            {listing.urls_image && listing.urls_image.length > 0 ? (
              <PropertyImage
                src={listing.urls_image[0]}
                alt={listing.title || translations.propertyImage}
                loading="lazy"
              />
            ) : (
              <ImagePlaceholder>
                <Icon name="home" size="xl" color="white" />
              </ImagePlaceholder>
            )}

            {listing.active !== undefined && (
              <StatusBadge $isActive={listing.active}>
                {listing.active ? translations.active : translations.expired}
              </StatusBadge>
            )}

            {onRemoveListing && (
              <RemoveButton
                variant="ghost"
                size="small"
                onClick={(e) => handleRemoveClick(e, listing.id)}
                title={translations.removeFromSaved}
                aria-label={translations.removeFromSaved}
              >
                <Icon name="unbookmark" size="medium" color="#6b7280" />
              </RemoveButton>
            )}
          </ImageContainer>

          <ContentSection>
            <PriceRow>
              <MainPrice>{formatPrice(listing.price)}</MainPrice>
              {formatPricePerM2(listing) && (
                <PricePerM2>CHF {formatPricePerM2(listing)}{translations.perSqm}</PricePerM2>
              )}
            </PriceRow>

            <PropertyTitle>
              {listing.title || listing.property_type || translations.propertyListing}
            </PropertyTitle>

            <AddressRow>
              <FeatureIcon name="map-pin" size="small" />
              <AddressText>
                {listing.address || translations.addressNotAvailable}
                {listing.postal_code &&
                  listing.city_name &&
                  `, ${listing.postal_code} ${listing.city_name}`}
              </AddressText>
            </AddressRow>

            <FeatureGrid>
              <FeatureItem>
                <FeatureValue>
                  {listing.rooms_nb && listing.rooms_nb > 0
                    ? listing.rooms_nb
                    : '-'}
                </FeatureValue>
                <FeatureLabel>{translations.rooms}</FeatureLabel>
              </FeatureItem>

              <FeatureItem>
                <FeatureValue>
                  {listing.area && listing.area > 0
                    ? Math.round(listing.area)
                    : '-'}
                </FeatureValue>
                <FeatureLabel>{translations.sqm}</FeatureLabel>
              </FeatureItem>

              <FeatureItem>
                <FeatureValue>
                  {listing.construction_year && listing.construction_year > 0
                    ? listing.construction_year
                    : '-'}
                </FeatureValue>
                <FeatureLabel>{translations.built}</FeatureLabel>
              </FeatureItem>
            </FeatureGrid>

            <DateRow>
              <FeatureIcon name="clock" size="small" />
              <DateText>
                {translations.listed} {formatDate(listing.listing_timestamp)}
              </DateText>
            </DateRow>
          </ContentSection>
        </ListingCard>
      ))}
    </GridContainer>
  )
}
